import { ComponentType } from 'react';
import { makeStyles } from 'tss-react/mui';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Warning as WarningIcon } from '@mui/icons-material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { MarkdownContent } from '@backstage/core-components';
import { useActiveBanners } from '../../hooks/useActiveBanners';
import type { Banner, BannerSeverity } from '../../types';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const severityConfig: Record<
  BannerSeverity,
  { bg: string; border: string; color: string; Icon: ComponentType<any> }
> = {
  high: {
    bg: '#FDEDED',
    border: '#F44336',
    color: '#5F2120',
    Icon: ErrorOutlineIcon,
  },
  medium: {
    bg: '#FFF4E5',
    border: '#FF9800',
    color: '#663C00',
    Icon: WarningIcon,
  },
  low: {
    bg: '#EDF7ED',
    border: '#4CAF50',
    color: '#1E4620',
    Icon: CheckCircleOutlineIcon,
  },
  info: {
    bg: '#E5F6FD',
    border: '#03A9F4',
    color: '#014361',
    Icon: InfoOutlinedIcon,
  },
};

const useStyles = makeStyles()({
  root: {
    width: '100%',
    flexShrink: 0,
  },
  banner: {
    display: 'flex',
    alignItems: 'flex-start',
    padding: '10px 16px',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
  },
  icon: {
    marginRight: 10,
    marginTop: 2,
    flexShrink: 0,
    fontSize: 20,
  },
  content: {
    flex: 1,
    fontSize: 14,
    lineHeight: 1.5,
  },
  title: {
    fontWeight: 600,
    marginRight: 6,
  },
  message: {
    opacity: 0.9,
    // MarkdownContent renders a div; keep it inline-flow with the title
    '& > div': { display: 'inline' },
    '& p': { display: 'inline', margin: 0 },
    '& a': {
      color: 'inherit',
      textDecoration: 'underline',
      textUnderlineOffset: '2px',
      transition: 'opacity 0.15s',
      '&:hover': { opacity: 0.7 },
    },
    '& strong, & b': { fontWeight: '700 !important' as any },
    '& em, & i': { fontStyle: 'italic !important' as any },
    '& del, & s': { opacity: 0.6 },
    '& code': {
      fontFamily: '"Roboto Mono", "Courier New", monospace',
      fontSize: '0.875em',
      padding: '1px 4px',
      borderRadius: 3,
      backgroundColor: 'rgba(0,0,0,0.1)',
    },
  },
  close: {
    marginLeft: 8,
    padding: 4,
    flexShrink: 0,
  },
});

// ---------------------------------------------------------------------------
// Single banner row
// ---------------------------------------------------------------------------

interface BannerRowProps {
  banner: Banner;
  onDismiss: (banner: Banner) => void;
}

function BannerRow({ banner, onDismiss }: BannerRowProps) {
  const { classes } = useStyles();
  const cfg = severityConfig[banner.severity] ?? severityConfig.info;
  const { Icon } = cfg;

  return (
    <div
      className={classes.banner}
      style={{ backgroundColor: cfg.bg, borderLeft: `4px solid ${cfg.border}`, color: cfg.color }}
      role="alert"
    >
      <Icon className={classes.icon} style={{ color: cfg.border }} />
      <div className={classes.content}>
        <span className={classes.title}>{banner.title}</span>
        <span className={classes.message}>
          <MarkdownContent content={banner.message} dialect="gfm" />
        </span>
      </div>
      {banner.dismissable && (
        <IconButton
          size="small"
          className={classes.close}
          aria-label="Dismiss banner"
          onClick={() => onDismiss(banner)}
          style={{ color: cfg.color }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BannerBar — renders above the app layout
// ---------------------------------------------------------------------------

export function BannerBar() {
  const { banners, dismiss } = useActiveBanners();

  if (banners.length === 0) {
    return null;
  }

  return (
    <div style={{ width: '100%' }}>
      {banners.map(banner => (
        <BannerRow key={banner.id} banner={banner} onDismiss={dismiss} />
      ))}
    </div>
  );
}
